import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProviderCard from "@/components/ProviderCard";
import CareRequestDialog from "@/components/CareRequestDialog";
import { Loader, Empty, PageHeader } from "@/components/Common";
import { api, useCatalog } from "@/lib/api";

const ALL = "all";
export default function Providers() {
  const { data: cat } = useCatalog();
  const [f, setF] = useState({ q: "", category: ALL, county: ALL, payment: ALL, accepting: false });
  const [dlg, setDlg] = useState(null);
  const params = Object.fromEntries(Object.entries({ ...f, accepting: f.accepting || undefined }).filter(([, v]) => v && v !== ALL));
  const { data, isLoading } = useQuery({ queryKey: ["providers", params], queryFn: () => api.get("/providers", { params }).then((r) => r.data) });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const sel = (k, ph, opts, testid) => (
    <Select value={f[k]} onValueChange={(v) => set(k, v)}>
      <SelectTrigger className="h-11 bg-white" data-testid={testid}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>{ph}</SelectItem>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <PageHeader eyebrow="Provider marketplace" title="Browse care providers" subtitle="Every listing shows whether the provider is currently accepting new clients." />
      <div className="cz-card p-4 grid gap-3 md:grid-cols-5 items-center" data-testid="provider-filters">
        <div className="relative md:col-span-1"><Search className="h-4 w-4 absolute left-3 top-3.5 text-slate-400" /><Input className="h-11 pl-9" placeholder="Search by name" value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="provider-search-input" /></div>
        {cat && sel("category", "All care types", cat.service_categories.map((c) => ({ v: c.id, l: c.name })), "filter-category")}
        {cat && sel("county", "All counties", cat.counties.map((c) => ({ v: c.id, l: `${c.name} County` })), "filter-county")}
        {cat && sel("payment", "Any payment", cat.payment_methods.map((p) => ({ v: p, l: p })), "filter-payment")}
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 px-2"><Switch checked={f.accepting} onCheckedChange={(v) => set("accepting", v)} data-testid="filter-accepting-switch" />Accepting clients only</label>
      </div>
      {isLoading ? <Loader /> : data.length === 0 ? <div className="mt-8"><Empty title="No providers match these filters" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="providers-grid">
          {data.map((p) => <ProviderCard key={p.id} p={p} onRequest={(prov, mode) => setDlg({ prov, mode })} />)}
        </div>
      )}
      {dlg && <CareRequestDialog provider={dlg.prov} mode={dlg.mode} careTypeId={f.category !== ALL ? f.category : null} onClose={() => setDlg(null)} />}
    </div>
  );
}
